'use client'

import { useEffect, useState } from 'react'
import { useCartVisualFeedback, useOptimisticUpdates } from '@/stores/optimized-cart-store'
import { useTranslations } from 'next-intl'

/**
 * Global cart operation feedback component
 * Shows toast-like notifications for cart operations with optimistic updates
 */
export function CartOperationFeedback() {
  const t = useTranslations('cart')
  const { hasError, lastError } = useCartVisualFeedback()
  const { hasPendingOperations, pendingCount } = useOptimisticUpdates()
  const [showNotification, setShowNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState('')
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'pending'>('success')

  // Handle error notifications
  useEffect(() => {
    if (hasError && lastError) {
      setNotificationMessage(lastError)
      setNotificationType('error')
      setShowNotification(true)
      
      // Auto-hide error after 5 seconds
      const timer = setTimeout(() => {
        setShowNotification(false)
      }, 5000)
      
      return () => clearTimeout(timer)
    }
  }, [hasError, lastError])

  // Handle pending operations feedback
  useEffect(() => {
    if (hasPendingOperations && pendingCount > 0) {
      setNotificationMessage(
        pendingCount === 1 
          ? t('feedback.updating', { defaultValue: 'Updating cart...' })
          : t('feedback.updatingMultiple', { 
              defaultValue: 'Updating {{count}} items...', 
              count: pendingCount 
            })
      )
      setNotificationType('pending')
      setShowNotification(true)
    } else if (!hasPendingOperations && showNotification && notificationType === 'pending') {
      // Operations completed successfully
      setNotificationMessage(t('feedback.updated', { defaultValue: 'Cart updated!' }))
      setNotificationType('success')
      
      // Auto-hide success after 2 seconds
      const timer = setTimeout(() => {
        setShowNotification(false)
      }, 2000)
      
      return () => clearTimeout(timer)
    }
  }, [hasPendingOperations, pendingCount, showNotification, notificationType, t])

  if (!showNotification) {
    return null
  }

  const getNotificationStyles = () => {
    switch (notificationType) {
      case 'error':
        return 'bg-status-error/90 text-white border-status-error'
      case 'pending':
        return 'bg-primary/90 text-white border-primary'
      case 'success':
        return 'bg-status-success/90 text-white border-status-success'
      default:
        return 'bg-surface border-border text-text-main'
    }
  }

  const getIcon = () => {
    switch (notificationType) {
      case 'error':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )
      case 'pending':
        return (
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
        )
      case 'success':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 pointer-events-none">
      <div
        className={`
          flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg border backdrop-blur-sm
          transform transition-all duration-300 ease-in-out
          ${showNotification ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}
          ${getNotificationStyles()}
        `}
      >
        {getIcon()}
        <span className="text-sm font-medium">{notificationMessage}</span>
        
        {/* Close button for error notifications */}
        {notificationType === 'error' && (
          <button
            onClick={() => setShowNotification(false)}
            className="ml-2 text-white/80 hover:text-white pointer-events-auto"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}